// T&M Tickets — foreman ticket entry + office tracking log (dark-launched module).
// Gated by REACT_APP_TM_ENABLED (see Dashboard.js). All data direct-to-Supabase
// like Requests/Time Off (authenticated RLS); catalogs come from tm_catalog_items
// (nightly Plexxis DataVerse sync + manual labor seed). No dollars anywhere —
// pricing happens in the Plexxis COR after the signed ticket comes back.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const RATE_VARIANTS = ['Straight', 'OT', 'DT', 'OTO', 'DTO']

const STATUS = {
  expected:          { label: 'Expected',           bg: '#FAEEDA', fg: '#854F0B' },
  draft:             { label: 'Draft',              bg: '#EEEEEE', fg: '#555555' },
  submitted:         { label: 'Submitted',          bg: '#E6F1FB', fg: '#0C447C' },
  out_for_signature: { label: 'Out for Signature',  bg: '#EEE6FB', fg: '#5B2D9E' },
  signed:            { label: 'Signed',             bg: '#E1F5EE', fg: '#085041' },
  co_created:        { label: 'CO Created',         bg: '#E2F4F1', fg: '#0F6E60' },
  sent_to_client:    { label: 'Sent to Client',     bg: '#EAF3DE', fg: '#3B6D11' },
  client_approved:   { label: 'Client Approved ✓',  bg: '#D9F2D9', fg: '#1B5E20' },
  void:              { label: 'Void',               bg: '#FBEAF0', fg: '#72243E' },
}

const OFFICE_ROLES = ['owner', 'road_super', 'admin', 'pm']

function chip(status) {
  const s = STATUS[status] || STATUS.draft
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
      background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>{s.label}</span>
  )
}

// Bordered tables for the ticket detail view (Patrick: easier to read than lines).
const dth = { textAlign: 'left', fontSize: 11, color: '#555', padding: '5px 8px', background: '#f5f7fa', border: '1px solid #e3e6ea', fontWeight: 600 }
const dtd = { fontSize: 13, padding: '5px 8px', border: '1px solid #e3e6ea' }
function DetailTable({ title, headers, rows }) {
  if (!rows.length) return null
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{title}</div>
      <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 860, background: '#fff' }}>
        <thead><tr>{headers.map((h, i) => <th key={i} style={dth}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((cells, i) => (
          <tr key={i}>{cells.map((c, j) => <td key={j} style={dtd}>{c}</td>)}</tr>
        ))}</tbody>
      </table>
    </div>
  )
}

function todayKey() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Ticket dates are 'YYYY-MM-DD'; board cells are keyed by toDateString() ("Thu Oct 29 2026").
// Noon local avoids any DST/UTC day-shift (the same conversion api/text/dispatch.js uses).
function boardKey(ymd) {
  const [y, m, d] = String(ymd || '').split('-').map(Number)
  return y && m && d ? new Date(y, m - 1, d, 12, 0, 0).toDateString() : ''
}
const shortDay = (ymd) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number)
  return y && m && d ? new Date(y, m - 1, d, 12).toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' }) : ''
}

// What the office sees after asking api/tm/notify?action=expected-text to text the foreman.
function expectedTextNotice(j) {
  const who = j?.foreman_name || 'the foreman'
  if (j?.texted) return { kind: 'ok', text: `Texted ${who} about ticket #${j.ticket_no}.` }
  const why = {
    texting_off: 'texting for T&M is turned off.',
    not_on_test_allowlist: `${who} is not on the T&M test texting list.`,
    no_foreman: 'the ticket has no foreman.',
    foreman_not_found: `no active employee is named ${who}.`,
    foreman_ambiguous: `two employees are named ${who}.`,
    no_phone: `${who} has no phone number on file.`,
    not_opted_in: `${who} hasn't opted in to texts - tell them directly.`,
    quiet_hours: `it's outside texting hours (7 AM - 8 PM). Tell ${who} directly, or use Text foreman again later.`,
    no_announce_line: 'the one-way texting line is not set up.',
    send_failed: `the text to ${who} failed${j?.detail ? ` (${j.detail})` : ''}.`,
  }[j?.reason]
  if (j?.reason === 'already_texted') return { kind: 'ok', text: `${who} was already texted about ticket #${j.ticket_no}.` }
  return { kind: 'warn', text: `Not texted: ${why || (j?.error || j?.reason || 'unknown problem') + '.'}` }
}

function daysSince(iso) {
  if (!iso) return null
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
}

// ── Signature pad (plain canvas, no deps) ─────────────────────────────────────
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const dirty = useRef(false)

  const pos = (e) => {
    const c = canvasRef.current
    const r = c.getBoundingClientRect()
    const t = e.touches ? e.touches[0] : e
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }
  }
  const start = (e) => {
    e.preventDefault()
    drawing.current = true
    const ctx = canvasRef.current.getContext('2d')
    const p = pos(e)
    ctx.beginPath(); ctx.moveTo(p.x, p.y)
  }
  const move = (e) => {
    if (!drawing.current) return
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#16305e'
    const p = pos(e)
    ctx.lineTo(p.x, p.y); ctx.stroke()
    dirty.current = true
  }
  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    if (dirty.current) onChange(canvasRef.current.toDataURL('image/png'))
  }
  const clear = () => {
    const c = canvasRef.current
    c.getContext('2d').clearRect(0, 0, c.width, c.height)
    dirty.current = false
    onChange(null)
  }

  return (
    <div>
      <canvas ref={canvasRef} width={560} height={160}
        style={{ width: '100%', maxWidth: 560, height: 160, border: '1px dashed #aab', borderRadius: 6, background: '#fff', touchAction: 'none' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <div style={{ marginTop: 4 }}>
        <button type="button" className="btn btn-sm" onClick={clear}>Clear signature</button>
      </div>
    </div>
  )
}

// ── Searchable catalog picker (in-page dropdown) ──────────────────────────────
// Vague descriptions need their Plexxis type/group for context ("Quickrete
// Nonshrink Precision Grout" → GROUT), so each row carries a label and a
// matched pick shows a type line under the input.
// Not a <datalist>: iPad Safari shows datalist suggestions in the keyboard bar,
// about 3 at a time, so most of the Plexxis list was out of reach. This is a
// free-text combobox - whatever is typed is still saved (custom items allowed);
// the list under the input only helps pick.
function typeLabel(it) {
  if (!it) return ''
  const parts = []
  if (it.group_name) parts.push(it.group_name)
  if (it.type_name && it.type_name !== it.group_name) parts.push(it.type_name)
  return parts.join(' · ')
}

const CATALOG_MAX_ROWS = 75 // more than this bogs down the iPad; typing narrows it

function CatalogPicker({ items, value, onPick, placeholder }) {
  const [open, setOpen] = useState(false)
  const [hi, setHi] = useState(-1)
  const boxRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const hit = items.find(m => m.name === value)
  // Same every-word rule as JobPicker, over the name and its Plexxis type/group.
  const matches = useMemo(() => {
    const toks = fold(value).split(/\s+/).filter(Boolean)
    if (!toks.length) return items
    return items.filter(it => {
      const hay = fold(`${it.name} ${it.group_name || ''} ${it.type_name || ''}`)
      return toks.every(t => hay.includes(t))
    })
  }, [items, value])
  const shown = matches.slice(0, CATALOG_MAX_ROWS)
  const more = matches.length - shown.length

  useEffect(() => {
    if (!open) return
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('touchstart', away)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('touchstart', away) }
  }, [open])

  const openList = () => {
    if (open) return
    setOpen(true)
    // On a tablet, lift the field toward the top so the list isn't under the keyboard.
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      setTimeout(() => { if (inputRef.current) inputRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' }) }, 300)
    }
  }
  const pick = (it) => {
    onPick(it.name)
    setOpen(false)
    setHi(-1)
    if (inputRef.current) inputRef.current.blur() // let the iPad keyboard drop
  }
  const moveTo = (i) => {
    setHi(i)
    const el = listRef.current && listRef.current.children[i]
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
  }
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); openList(); moveTo(Math.min(hi + 1, shown.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveTo(Math.max(hi - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && shown[hi]) pick(shown[hi]); else setOpen(false) }
    else if (e.key === 'Escape' || e.key === 'Tab') { setOpen(false) }
  }
  const keep = (e) => e.preventDefault() // a tap on the list must not blur the input first

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input ref={inputRef} className="form-input" value={value} placeholder={placeholder}
        autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
        onFocus={openList}
        onChange={e => { onPick(e.target.value); setHi(-1); openList() }}
        onKeyDown={onKey}
        style={{ width: '100%' }} />
      {open && (
        <div ref={listRef} style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 2,
          background: '#fff', border: '1px solid #cfd5dc', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,.12)',
          maxHeight: 280, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {shown.map((it, i) => (
            <div key={it.id} onMouseDown={keep} onClick={() => pick(it)} onMouseEnter={() => setHi(i)}
              style={{ minHeight: 44, padding: '6px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                cursor: 'pointer', borderBottom: '1px solid #f0f2f4',
                background: i === hi ? '#EEF4FB' : it.name === value ? '#f6f8fa' : '#fff' }}>
              <span style={{ fontSize: 14 }}>{it.name}</span>
              {typeLabel(it) && <span style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{typeLabel(it)}</span>}
            </div>
          ))}
          {more > 0 && (
            <div onMouseDown={keep} style={{ padding: '10px 12px', color: '#888', fontSize: 13 }}>
              Keep typing to narrow — {more} more
            </div>
          )}
          {shown.length === 0 && (
            <div onMouseDown={keep} style={{ padding: '10px 12px', color: '#888', fontSize: 13 }}>
              No match in the list — what you typed will be saved as is
            </div>
          )}
        </div>
      )}
      {hit && typeLabel(hit) && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{typeLabel(hit)}</div>
      )}
    </div>
  )
}

// ── Print / Save-PDF (mirrors the GoFormz T&M sheet; browser print → Save as PDF) ──
function esc(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

// The signature is injected into an img src attribute; only accept genuine
// canvas toDataURL output (tm_tickets is authenticated-writable, so the column
// value is not guaranteed to be canvas-generated).
function isDataImage(s) {
  return typeof s === 'string' && /^data:image\/(png|jpe?g);base64,[A-Za-z0-9+/=]+$/.test(s)
}

// Cornerstone 'CC' logo (new version supplied by Patrick 8/19/2026), embedded
// so the print view needs no network fetch.
const TM_LOGO = 'data:image/png;base64,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'

function printTicket(t) {
  const laborRows = (t.labor || []).map(r => `<tr>
      <td>${esc(r.labor_class)}</td><td>${esc(r.date_of_work || t.ticket_date)}</td><td>${esc(r.rate_variant)}</td>
      <td class="n">${esc(r.num_men)}</td><td class="n">${esc(r.hours_per_man)}</td><td class="n">${esc(r.total_hours)}</td>
    </tr>`).join('')
  const matRows = (t.materials || []).map(r => `<tr>
      <td>${esc(r.description)}</td><td class="n">${esc(r.uom || '')}</td><td class="n">${esc(r.qty)}</td>
    </tr>`).join('')
  const eqRows = (t.equipment || []).map(r => `<tr>
      <td>${esc(r.description)}</td><td class="n">${esc(r.hours_of_use)}</td><td class="n">${esc(r.qty)}</td><td class="n">${esc(r.total_hours)}</td>
    </tr>`).join('')
  const blank = (n, cols) => Array.from({ length: n }, () => `<tr>${'<td>&nbsp;</td>'.repeat(cols)}</tr>`).join('')

  // Chrome names the saved PDF after the document title — job first so the
  // office can file it against the right job at a glance.
  // Patrick's exact format: "582 \u2013 Target Marlborough MA - T&M 4501"
  const fileName = [[t.job_num, t.job_name].filter(Boolean).join(' \u2013 '), `T&M ${t.ticket_no}`]
    .filter(Boolean).join(' - ').replace(/[\\/:*?"<>|]/g, '-')

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(fileName)}</title>
  <style>
    body{font-family:'Times New Roman',Georgia,serif;color:#111;margin:0;padding:34px 40px;font-size:13px}
    .hd{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #16305e;padding-bottom:10px}
    .logo{display:flex;gap:10px;align-items:center}
    .logoimg{width:74px;height:74px;border-radius:8px}
    .co{font-size:23px;font-weight:700;text-decoration:underline}
    .co-sub{font-size:12.5px;color:#222;margin-top:3px;line-height:1.4}
    .tm{text-align:right}
    .tm-title{font-size:23px;font-weight:800}
    .tm-no{font-size:17px;font-weight:800;margin-top:5px}
    .tm-no span{color:#c00;text-decoration:underline}
    .meta{margin:12px 0 4px;font-size:13px}
    .meta b{font-weight:700}
    .sec{margin-top:14px}
    .sec-title{font-weight:800;font-size:13.5px;text-decoration:underline;margin-bottom:3px}
    table{width:100%;border-collapse:collapse;font-size:12px}
    th{border:1px solid #111;padding:4px 6px;background:#eef1f5;font-size:11px;text-align:center}
    td{border:1px solid #111;padding:4px 6px}
    td.n{text-align:center;width:70px}
    .appr{margin-top:18px;border:1px solid #111;padding:10px 14px;display:flex;gap:24px}
    .appr-left{flex:1}
    .appr-row{margin:7px 0;font-size:13px}
    .sig-img{max-height:60px;max-width:260px;vertical-align:middle}
    .sig-line{display:inline-block;border-bottom:1px solid #111;min-width:240px;padding:0 6px}
    .cmt{flex:1;font-size:12px}
    .cmt-title{text-decoration:underline;font-size:12px;margin-bottom:4px}
    thead{display:table-header-group} tr{page-break-inside:avoid} .appr{page-break-inside:avoid}
    @media print{body{padding:52px 58px}} /* ~0.75in baked in — immune to the dialog's margin setting */
  </style></head><body>
  <div class="hd">
    <div class="logo">
      <img class="logoimg" src="${TM_LOGO}" alt="CC"/>
      <div>
        <div class="co">Cornerstone Contracting, Inc</div>
        <div class="co-sub">224 E. Highland Pkwy &nbsp;&nbsp;Phone: (908) 298-1165<br/>Roselle, NJ 07203 &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Fax: (908) 298-1167</div>
      </div>
    </div>
    <div class="tm">
      <div class="tm-title">Time &amp; Material</div>
      <div class="tm-no">Ticket No: <span>${esc(t.ticket_no)}</span></div>
    </div>
  </div>
  <div class="meta"><b>Date:</b> <u>&nbsp;${esc(t.ticket_date)}&nbsp;</u> &nbsp; <b>Job #:</b> <u>&nbsp;${esc(t.job_num || '')}&nbsp;</u> &nbsp; <b>Client / Project:</b> <u>&nbsp;${esc(t.client_project || `${t.job_num || ''} – ${t.job_name || ''}`)}&nbsp;</u></div>
  <div class="meta"><b><u>Description &amp; Location:</u></b><br/>${esc(t.description).replace(/\n/g, '<br/>')}${t.location ? '<br/>' + esc(t.location) : ''}</div>

  <div class="sec"><div class="sec-title">Labor</div>
  <table><thead><tr><th style="text-align:left"></th><th>Date of Work</th><th>Rate</th><th># of Men</th><th>Hours Per Man</th><th>Total Hours</th></tr></thead>
  ${laborRows}${blank(Math.max(0, 4 - (t.labor || []).length), 6)}</table></div>

  <div class="sec"><div class="sec-title">Material</div>
  <table><thead><tr><th style="text-align:left"></th><th>UOM (YDS, FT, EA, ETC)</th><th># of Items</th></tr></thead>
  ${matRows}${blank(Math.max(0, 4 - (t.materials || []).length), 3)}</table></div>

  <div class="sec"><div class="sec-title">Equipment</div>
  <table><thead><tr><th style="text-align:left"></th><th>Hours of Use</th><th># of Items</th><th>Total Hours</th></tr></thead>
  ${eqRows}${blank(Math.max(0, 3 - (t.equipment || []).length), 4)}</table></div>

  <div class="appr">
    <div class="appr-left">
      <div class="appr-row">Approved? &nbsp;${t.status === 'signed' || t.signed_at || ['co_created','sent_to_client','client_approved'].includes(t.status) ? 'Yes' : ''}</div>
      <div class="appr-row">Approved By: &nbsp;<span class="sig-line">${isDataImage(t.signature_data) ? `<img class="sig-img" src="${esc(t.signature_data)}" alt="signature"/>` : '&nbsp;'}</span></div>
      <div class="appr-row">Print Name: &nbsp;<span class="sig-line">${esc(t.signer_name || '')}</span></div>
    </div>
    <div class="cmt"><div class="cmt-title">Client Comments</div>${esc(t.client_comments || '')}</div>
  </div>
  </body></html>`

  const win = window.open('', '_blank')
  if (win) {
    win.document.write(html)
    win.document.close()
    setTimeout(() => win.print(), 400) // print dialog → "Save as PDF" to attach to the COR
  } else {
    window.alert('Pop-up blocked — allow pop-ups for this site, then hit Print / PDF again.')
  }
}

// Jobs in job-number order: numeric numbers ascending (559 before 1002), then any
// non-numeric number (e.g. SHOP) alphabetically after them.
function jobCmp(a, b) {
  const na = /^\d+$/.test(String(a.num ?? '')) ? Number(a.num) : Infinity
  const nb = /^\d+$/.test(String(b.num ?? '')) ? Number(b.num) : Infinity
  if (na !== nb) return na - nb
  return String(a.num ?? '').localeCompare(String(b.num ?? '')) || String(a.name ?? '').localeCompare(String(b.name ?? ''))
}

const fold = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

// Searchable job picker for the ticket editor. Tap to open, type any part of the
// job number or name (every word must match, accents ignored), tap a job or press
// Enter for the first match. The list stays in job-number order.
// allLabel (list-view filter only): adds a top row that clears the pick, shown as
// the button text when nothing is picked.
function JobPicker({ jobs, value, onPick, allLabel }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(0)
  const boxRef = useRef(null)
  const listRef = useRef(null)
  const sorted = useMemo(() => [...(jobs || [])].sort(jobCmp), [jobs])
  const matches = useMemo(() => {
    const toks = fold(q).split(/\s+/).filter(Boolean)
    if (!toks.length) return sorted
    return sorted.filter(j => {
      const hay = fold(`${j.num} ${j.name} ${j.gc || ''} ${j.city || ''}`)
      return toks.every(t => hay.includes(t))
    })
  }, [sorted, q])
  const sel = sorted.find(j => String(j.id) === String(value))

  useEffect(() => {
    if (!open) return
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('touchstart', away)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('touchstart', away) }
  }, [open])

  const pick = (j) => { onPick(String(j.id)); setOpen(false); setQ('') }
  const moveTo = (i) => {
    setHi(i)
    const offset = allLabel && !q.trim() ? 1 : 0 // the "All jobs" row sits above the matches
    const el = listRef.current && listRef.current.children[i + offset]
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
  }
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); moveTo(Math.min(hi + 1, matches.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveTo(Math.max(hi - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (matches[hi]) pick(matches[hi]) }
    else if (e.key === 'Escape') { setOpen(false) }
  }

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      {open ? (
        <input className="form-input" autoFocus value={q} placeholder="Type job # or name…"
          onChange={e => { setQ(e.target.value); setHi(0) }} onKeyDown={onKey} style={{ width: '100%' }} />
      ) : (
        <button type="button" className="form-input"
          style={{ width: '100%', textAlign: 'left', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => { setQ(''); setHi(0); setOpen(true) }}>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: sel || value || allLabel ? 'inherit' : '#888' }}>
            {sel ? `${sel.num} – ${sel.name}` : value ? 'Job not in the active list' : (allLabel || 'Select job…')}
          </span>
          <span style={{ color: '#888', marginLeft: 6 }}>▾</span>
        </button>
      )}
      {open && (
        <div ref={listRef} style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 2,
          background: '#fff', border: '1px solid #cfd5dc', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,.12)',
          maxHeight: 320, overflowY: 'auto' }}>
          {allLabel && !q.trim() && (
            <div onClick={() => { onPick(''); setOpen(false) }}
              style={{ padding: '10px 12px', fontSize: 14, cursor: 'pointer', borderBottom: '1px solid #e3e6ea',
                fontWeight: 600, background: !value ? '#f6f8fa' : '#fff' }}>
              {allLabel}
            </div>
          )}
          {matches.length === 0 && (
            <div style={{ padding: '10px 12px', color: '#888', fontSize: 13 }}>No active job matches “{q}”</div>
          )}
          {matches.map((j, i) => (
            <div key={j.id} onClick={() => pick(j)} onMouseEnter={() => setHi(i)}
              style={{ padding: '10px 12px', fontSize: 14, cursor: 'pointer', borderBottom: '1px solid #f0f2f4',
                background: i === hi ? '#EEF4FB' : String(j.id) === String(value) ? '#f6f8fa' : '#fff' }}>
              <b>{j.num}</b> – {j.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// DocuSign envelope states as the office sees them (api/tm/docusign.js keeps them current).
const DS_LABEL = {
  sent: 'Waiting on the GC', delivered: 'GC opened it - waiting for signature', completed: 'Signed in DocuSign',
  declined: 'GC declined', voided: 'Voided',
}
const dsActive = (t) => !!t?.docusign_envelope_id && ['sent', 'delivered'].includes(t?.docusign_status)
// Mirrors canSend() in api/tm/docusign.js (the server re-checks).
const dsCanSend = (t) => !dsActive(t) && ['submitted', 'out_for_signature'].includes(t?.status)

// Why the database refused a write, as a sentence. The tm_tickets guard trigger raises
// readable messages; a plain row-level-security refusal names a table, so it is reworded.
const whyRefused = (e) => /row-level security/i.test(e?.message || '')
  ? "you don't have permission to change this ticket" : (e?.message || 'unknown error')

// Result of the office email (green) or a failure the foreman must act on (amber).
function NoticeBar({ notice, onClose, onRetry }) {
  if (!notice) return null
  const ok = notice.kind === 'ok'
  return (
    <div style={{ background: ok ? '#E1F5EE' : '#FFF4E5', color: ok ? '#085041' : '#8A4B00',
      border: `1px solid ${ok ? '#b9e4d3' : '#f3d19c'}`, padding: '8px 12px', borderRadius: 6,
      marginBottom: 10, fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ flex: 1, minWidth: 200 }}>{ok ? '✉️ ' : '⚠ '}{notice.text}</span>
      {notice.retryId && <button className="btn btn-sm" onClick={onRetry}>Try again</button>}
      <button className="btn btn-sm" onClick={onClose}>✕</button>
    </div>
  )
}

// ── Main view ─────────────────────────────────────────────────────────────────
export default function TMTicketsView({ session, role, activeJobs, cellData, roster, userName, userEmail, openTicketId }) {
  const userId = session?.user?.id
  const isOffice = OFFICE_ROLES.includes(role)
  const [view, setView] = useState('list') // list | edit | detail
  const [tickets, setTickets] = useState([])
  const [catalog, setCatalog] = useState({ materials: [], equipment: [], laborBases: [] })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [statusFilter, setStatusFilter] = useState('open')
  const [jobFilter, setJobFilter] = useState('')
  const [current, setCurrent] = useState(null)   // detail ticket (with lines + events)
  const [form, setForm] = useState(null)         // editor state
  const [error, setError] = useState('')
  const [notice, setNotice] = useState(null)     // office-email result banner
  const [ds, setDs] = useState(null)             // "Send to GC via DocuSign" form on the detail view

  const loadTickets = useCallback(async () => {
    const { data, error } = await supabase.from('tm_tickets').select('*').order('ticket_no', { ascending: false })
    if (error) { console.error('tm loadTickets:', error); return }
    setTickets(data || [])
  }, [])

  useEffect(() => {
    async function boot() {
      setLoading(true)
      const { data: cat, error: catErr } = await supabase
        .from('tm_catalog_items')
        .select('id, item_type, name, uom, group_name, type_name, is_active')
        .eq('is_active', true)
      if (catErr) console.error('tm catalog:', catErr)
      const rows = cat || []
      const materials = rows.filter(r => r.item_type === 'material' && r.group_name !== 'EQUIPMENT')
        .sort((a, b) => a.name.localeCompare(b.name))
      // Chargeable equipment comes from Plexxis with DAY/WEEK/MONTH rental
      // variants ("Articulating Boom Lift - DAY/-WEEK/-MONTH"). The ticket only
      // records hours of use — the rental-period pricing choice happens at COR
      // time in Plexxis — so collapse to one entry per base name. The kept
      // catalog_item_id is a representative variant (used for type lookups).
      const eqMap = new Map()
      rows.filter(r => r.item_type === 'material' && r.group_name === 'EQUIPMENT').forEach(r => {
        const base = r.name.replace(/\s*-\s*(DAY|WEEK|MONTH|DAILY|WEEKLY|MONTHLY)\s*$/i, '').trim()
        if (base && !eqMap.has(base)) {
          eqMap.set(base, { ...r, name: base })
        }
      })
      const equipment = [...eqMap.values()].sort((a, b) => a.name.localeCompare(b.name))
      // Labor: one entry per base class (type_name); variant is picked separately.
      const seen = new Set()
      const laborBases = []
      rows.filter(r => r.item_type === 'labor').forEach(r => {
        const base = r.type_name || r.name
        if (!seen.has(base)) { seen.add(base); laborBases.push(base) }
      })
      setCatalog({ materials, equipment, laborBases })
      await loadTickets()
      setLoading(false)
    }
    boot()
  }, [loadTickets])

  // ── Events helper ───────────────────────────────────────────────────────────
  const logEvent = useCallback(async (ticketId, event, detail) => {
    const { error } = await supabase.from('tm_ticket_events').insert({
      ticket_id: ticketId, event, detail: detail || null,
      created_by: userId, created_by_name: userName || userEmail,
    })
    if (error) {
      console.error('tm logEvent:', error)
      setNotice({ kind: 'warn', text: `Couldn't save the ticket history entry: ${whyRefused(error)}` })
    }
  }, [userId, userName, userEmail])

  // Emails the office (job PM + owners) about a ticket that was just sent to the
  // office or signed in the field. The server picks the email from the ticket's
  // status and sends each kind at most once (api/tm/notify.js). Never undoes the
  // save - a failure is shown with a Try again button.
  const notifyOffice = useCallback(async (ticketId) => {
    const fail = (why) => setNotice({ kind: 'warn', retryId: ticketId,
      text: `The ticket is saved, but the office email did not go out${why ? ` (${why})` : ''}. Try again, or call the office.` })
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const r = await fetch('/api/tm/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.access_token || ''}` },
        body: JSON.stringify({ ticket_id: ticketId }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.ok) { console.error('tm notify:', r.status, j); fail(j.error || r.status); return }
      if (j.sent) {
        const n = (j.to || []).length
        setNotice({ kind: 'ok', text: `Office emailed${j.kind === 'signed' ? ' the signed ticket' : ' for signature'} (${n} ${n === 1 ? 'person' : 'people'}).` })
      } else if (j.already) {
        setNotice({ kind: 'ok', text: 'The office was already emailed about this ticket.' })
      }
    } catch (e) {
      console.error('tm notify:', e)
      fail('')
    }
  }, [])

  // Texts the foreman named on an Expected ticket (api/tm/notify?action=expected-text).
  // The server decides everything (switch, consent, quiet hours, once per foreman); this
  // only shows the result. A failed text never undoes the save.
  const textForeman = useCallback(async (ticketId, force = false) => {
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const r = await fetch('/api/tm/notify?action=expected-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.access_token || ''}` },
        body: JSON.stringify({ ticket_id: ticketId, force }),
      })
      const j = await r.json().catch(() => ({}))
      setNotice(expectedTextNotice(j))
    } catch (e) {
      console.error('tm expected-text:', e)
      setNotice({ kind: 'warn', text: 'Not texted: could not reach Crew Dispatch.' })
    }
  }, [])

  // ── Editor ──────────────────────────────────────────────────────────────────
  // An office user (PM, owner...) is not the foreman, so their new tickets start with the
  // Foreman field BLANK and it fills in from the board (foreman_auto); a foreman's own new
  // ticket still names himself.
  function blankForm(seed) {
    return {
      id: null,
      job_id: '', ticket_date: todayKey(), description: '', location: '',
      foreman_name: isOffice ? '' : (userName || ''), status: 'draft',
      foreman_auto: false, foreman_err: '', orig_foreman: '',
      labor: [], materials: [], equipment: [],
      ...seed,
    }
  }

  function openNew() { setForm(blankForm()); setError(''); setNotice(null); setView('edit') }

  async function openEdit(t) {
    const lines = await loadLines(t.id)
    setForm({
      id: t.id, ticket_no: t.ticket_no, job_id: t.job_id ?? '', ticket_date: t.ticket_date,
      description: t.description || '', location: t.location || '',
      foreman_name: t.foreman_name || '', status: t.status,
      foreman_auto: false, foreman_err: '', orig_foreman: t.foreman_name || '',
      ...lines,
    })
    setError(''); setView('edit')
  }

  async function loadLines(ticketId) {
    const [la, ma, eq] = await Promise.all([
      supabase.from('tm_ticket_labor').select('*').eq('ticket_id', ticketId).order('sort_order'),
      supabase.from('tm_ticket_materials').select('*').eq('ticket_id', ticketId).order('sort_order'),
      supabase.from('tm_ticket_equipment').select('*').eq('ticket_id', ticketId).order('sort_order'),
    ])
    return { labor: la.data || [], materials: ma.data || [], equipment: eq.data || [] }
  }

  // On a NEW ticket the foreman follows the board for the picked job + date, but only while
  // the field is blank or still holds the previous auto-filled name - a name picked by hand
  // is never overwritten.
  function autoForeman(f, jobId, dateKey) {
    if (f.id || !(f.foreman_name === '' || f.foreman_auto)) return f
    const fm = cellData?.[Number(jobId)]?.[boardKey(dateKey)]?.foreman || ''
    return { ...f, foreman_name: fm, foreman_auto: !!fm, foreman_err: '' }
  }

  // Prefill from the schedule when job changes: crew count + foreman that day.
  function applyJobDefaults(f, jobId, dateKey) {
    const cell = cellData?.[Number(jobId)]?.[boardKey(dateKey)]
    const next = autoForeman({ ...f, job_id: jobId }, jobId, dateKey)
    if (cell) {
      if (!next.foreman_name && cell.foreman) next.foreman_name = cell.foreman
      if ((f.labor || []).length === 0 && cell.count > 0) {
        next.labor = [{ labor_class: '', rate_variant: 'Straight', date_of_work: dateKey,
          num_men: cell.count, hours_per_man: 8, total_hours: cell.count * 8, sort_order: 0 }]
      }
    }
    return next
  }

  const setLine = (kind, idx, patch) => setForm(f => {
    const rows = [...f[kind]]
    rows[idx] = { ...rows[idx], ...patch }
    if (kind === 'labor' || kind === 'equipment') {
      const r = rows[idx]
      const a = kind === 'labor' ? Number(r.num_men) || 0 : Number(r.qty) || 0
      const b = kind === 'labor' ? Number(r.hours_per_man) || 0 : Number(r.hours_of_use) || 0
      rows[idx].total_hours = Math.round(a * b * 100) / 100
    }
    return { ...f, [kind]: rows }
  })

  const addLine = (kind) => setForm(f => ({
    ...f,
    [kind]: [...f[kind],
      kind === 'labor' ? { labor_class: '', rate_variant: 'Straight', date_of_work: f.ticket_date, num_men: 1, hours_per_man: 8, total_hours: 8, sort_order: f[kind].length }
      : kind === 'materials' ? { description: '', uom: '', qty: 1, sort_order: f[kind].length }
      : { description: '', hours_of_use: 8, qty: 1, total_hours: 8, sort_order: f[kind].length }],
  }))

  const dropLine = (kind, idx) => setForm(f => ({ ...f, [kind]: f[kind].filter((_, i) => i !== idx) }))

  // A ticket going to the office or to the GC must say what was done and carry at
  // least one line - nobody should be handed a blank ticket to sign.
  function completeError(f) {
    if (!f.description.trim()) return 'Describe the extra work before the ticket goes out for signature.'
    const hasLine = f.labor.some(r => r.labor_class) || f.materials.some(r => r.description) ||
      f.equipment.some(r => r.description)
    if (!hasLine) return 'Add at least one labor, material or equipment line before the ticket goes out for signature.'
    return null
  }

  // Returns the ticket id on success, null on failure. { complete } runs the
  // completeness check; { stay } keeps the caller on its screen (field signing).
  async function saveTicket(newStatus, { complete = false, stay = false } = {}) {
    if (!form.job_id) { setError('Pick a job.'); return null }
    // Office tickets must name the foreman - he is the one who can fill it in and who gets
    // the text. A NEW name must be a roster employee; an older saved value is left alone.
    if (isOffice) {
      const fm = form.foreman_name.trim()
      const changed = !form.id || fm !== (form.orig_foreman || '').trim()
      const onRoster = (roster || []).some(e => (e.name || '').trim().toLowerCase() === fm.toLowerCase())
      const msg = !fm ? 'Pick the foreman' : (changed && !onRoster ? 'Pick the foreman from the list' : '')
      if (msg) { setForm(f => ({ ...f, foreman_err: msg })); setError(msg + '.'); return null }
    }
    if (complete || newStatus === 'submitted') {
      const msg = completeError(form)
      if (msg) { setError(msg); return null }
    }
    setSaving(true); setError('')
    const job = activeJobs.find(j => j.id === Number(form.job_id))
    const head = {
      job_id: Number(form.job_id),
      job_num: job ? String(job.num) : null,
      job_name: job ? job.name : null,
      client_project: job ? `${job.gc ? job.gc + ' - ' : ''}${job.name}` : null,
      ticket_date: form.ticket_date,
      description: form.description.trim(),
      location: form.location.trim(),
      foreman_name: form.foreman_name.trim(),
      status: newStatus || form.status,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    }
    let ticketId = form.id
    // The database only lets a NEW ticket start as draft or expected, so a ticket sent
    // straight to the office is inserted as a draft and flipped to submitted below.
    let insertedAs = null
    if (!ticketId) {
      insertedAs = head.status === 'expected' ? 'expected' : 'draft'
      const { data, error } = await supabase.from('tm_tickets')
        .insert({ ...head, status: insertedAs, created_by: userId, created_by_name: userName || userEmail })
        .select().single()
      if (error) { setError("Couldn't save: " + whyRefused(error)); setSaving(false); return null }
      ticketId = data.id
      // The row exists now: a re-click after a later failure must update it, not insert another.
      setForm(f => ({ ...f, id: ticketId }))
      await logEvent(ticketId, head.status === 'expected' ? 'created_expected' : 'created', { ticket_no: data.ticket_no })
    } else {
      // .select() so a write the database silently filtered out (0 rows) is not taken as saved.
      const { data, error } = await supabase.from('tm_tickets').update(head).eq('id', ticketId).select('id')
      const err = error || (!data || data.length === 0 ? { message: 'row-level security' } : null)
      if (err) { setError("Couldn't save: " + whyRefused(err)); setSaving(false); return null }
    }
    // Replace line items wholesale (single-editor tool; last save wins).
    for (const [kind, table] of [['labor', 'tm_ticket_labor'], ['materials', 'tm_ticket_materials'], ['equipment', 'tm_ticket_equipment']]) {
      const del = await supabase.from(table).delete().eq('ticket_id', ticketId)
      if (del.error) { setError("Couldn't save: " + whyRefused(del.error)); setSaving(false); return null }
      const rows = form[kind]
        .filter(r => kind === 'labor' ? r.labor_class : r.description)
        .map((r, i) => {
          const { id, ...rest } = r
          return { ...rest, ticket_id: ticketId, sort_order: i }
        })
      if (rows.length) {
        const ins = await supabase.from(table).insert(rows)
        if (ins.error) { setError("Couldn't save: " + whyRefused(ins.error)); setSaving(false); return null }
      }
    }
    if (insertedAs && insertedAs !== head.status) {
      const { data, error } = await supabase.from('tm_tickets')
        .update({ status: head.status, updated_by: userId, updated_at: new Date().toISOString() })
        .eq('id', ticketId).select('id')
      const err = error || (!data || data.length === 0 ? { message: 'row-level security' } : null)
      if (err) { setError("Couldn't save: " + whyRefused(err)); setSaving(false); return null }
    }
    if (newStatus === 'submitted' && form.status !== 'submitted') {
      await logEvent(ticketId, 'submitted')
      await notifyOffice(ticketId) // "needs GC signature" email to the office
    }
    // The office staged (or re-assigned) an Expected ticket: text the foreman it names.
    if (isOffice && head.status === 'expected' &&
        (!form.id || head.foreman_name !== (form.orig_foreman || '').trim())) {
      await textForeman(ticketId)
    }
    setSaving(false)
    await loadTickets()
    if (!stay) setView('list')
    return ticketId
  }

  // "Have client sign now": save the ticket, then hand the device to the GC on a
  // review-and-sign screen. The status stays draft (or submitted) until a
  // signature is actually captured, so backing out leaves nothing half-done.
  async function startFieldSign() {
    const keep = form.status === 'submitted' ? 'submitted' : 'draft'
    const id = await saveTicket(keep, { complete: true, stay: true })
    if (!id) return
    const { data } = await supabase.from('tm_tickets').select('*').eq('id', id).single()
    if (!data) { setView('list'); return }
    const lines = await loadLines(id)
    setCurrent({ ...data, ...lines, events: [], sig: null, sigName: '', sigComments: data.client_comments || '' })
    setView('sign')
  }

  // ── Detail + transitions ────────────────────────────────────────────────────
  async function openDetail(t) {
    const lines = await loadLines(t.id)
    const { data: events } = await supabase.from('tm_ticket_events')
      .select('*').eq('ticket_id', t.id).order('created_at', { ascending: true })
    setCurrent({ ...t, ...lines, events: events || [], sig: null, sigName: '', sigComments: t.client_comments || '' })
    setDs(null)
    setView('detail')
  }

  // ── DocuSign (office only; api/tm/docusign.js) ─────────────────────────────
  async function dsCall(method, action, payload = {}) {
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const auth = { Authorization: `Bearer ${s?.access_token || ''}` }
      const r = method === 'GET'
        ? await fetch(`/api/tm/docusign?action=${action}&${new URLSearchParams(payload)}`, { headers: auth })
        : await fetch(`/api/tm/docusign?action=${action}`, {
            method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      const j = await r.json().catch(() => ({}))
      return { ...j, ok: r.ok && !!j.ok, httpStatus: r.status }
    } catch (e) {
      console.error('tm docusign:', e)
      return { ok: false, error: 'network', detail: 'Could not reach Crew Dispatch - check the connection.' }
    }
  }

  async function refreshCurrent(id) {
    await loadTickets()
    const { data } = await supabase.from('tm_tickets').select('*').eq('id', id).single()
    if (data) await openDetail(data)
  }

  // Pre-fills the GC signer from this ticket, else the last DocuSign signer on the same job.
  async function openDsForm(t) {
    let name = t.gc_signer_name || ''
    let email = t.gc_signer_email || ''
    if (!email && t.job_id != null) {
      const { data } = await supabase.from('tm_tickets').select('gc_signer_name, gc_signer_email')
        .eq('job_id', t.job_id).not('gc_signer_email', 'is', null)
        .order('docusign_sent_at', { ascending: false, nullsFirst: false }).limit(1)
      if (data && data[0]) { name = data[0].gc_signer_name || ''; email = data[0].gc_signer_email || '' }
    }
    setDs({ name, email, message: '', errors: {}, err: '', busy: false })
  }

  async function sendDs() {
    const form = ds
    setDs(d => ({ ...d, busy: true, err: '', errors: {} }))
    const j = await dsCall('POST', 'send', { ticket_id: current.id, signer_name: form.name, signer_email: form.email, message: form.message })
    if (!j.ok) {
      setDs(d => ({ ...d, busy: false, errors: j.errors || {}, err: j.errors ? '' : (j.detail || j.error || 'DocuSign send failed.') }))
      return
    }
    await refreshCurrent(current.id)
    setNotice({ kind: 'ok', text: `Sent to ${form.name} for signature through DocuSign${j.reply_to ? ` - replies go to ${j.reply_to}` : ''}.${j.webhook ? '' : ' Automatic updates are off for this envelope - use Check DocuSign status.'}` })
  }

  async function checkDs(t) {
    setSaving(true)
    const j = await dsCall('POST', 'sync', { ticket_id: t.id })
    await refreshCurrent(t.id)
    setSaving(false)
    if (!j.ok) setNotice({ kind: 'warn', text: `Could not check DocuSign (${j.detail || j.error}).` })
    else setNotice({ kind: 'ok', text: j.changed ? `Updated from DocuSign: ${DS_LABEL[j.status] || j.status}.` : `No change - DocuSign says: ${DS_LABEL[j.status] || j.status}.` })
  }

  async function voidDs(t) {
    const reason = window.prompt('Void this DocuSign envelope? The GC can no longer sign it. Reason (the GC sees this):',
      'Voided by Cornerstone - a corrected ticket will follow')
    if (reason === null) return
    setSaving(true)
    const j = await dsCall('POST', 'void', { ticket_id: t.id, reason })
    await refreshCurrent(t.id)
    setSaving(false)
    setNotice(j.ok ? { kind: 'ok', text: 'Envelope voided. The ticket is back to Submitted - edit it if needed and send it again.' }
      : { kind: 'warn', text: `Could not void the envelope (${j.detail || j.error}).` })
  }

  async function openSignedPdf(t) {
    const w = window.open('', '_blank') // opened before the await so iPad Safari allows it
    const j = await dsCall('GET', 'signed-pdf', { ticket_id: t.id })
    if (j.ok && j.url) { if (w) w.location = j.url; else window.location.assign(j.url) }
    else { if (w) w.close(); setNotice({ kind: 'warn', text: 'The signed PDF is not available.' }) }
  }

  async function transition(t, patch, event, detail) {
    setSaving(true)
    const { data: moved, error } = await supabase.from('tm_tickets')
      .update({ ...patch, updated_by: userId, updated_at: new Date().toISOString() })
      .eq('id', t.id).select('id')
    const err = error || (!moved || moved.length === 0 ? { message: 'row-level security' } : null)
    if (err) {
      console.error('tm transition:', err)
      setNotice({ kind: 'warn', text: "Couldn't save: " + whyRefused(err) })
      setSaving(false); return
    }
    await logEvent(t.id, event, detail)
    // Foreman-side finishes email the office; office-side status moves do not.
    if (event === 'submitted' || event === 'signed_in_field') await notifyOffice(t.id)
    await loadTickets()
    const { data } = await supabase.from('tm_tickets').select('*').eq('id', t.id).single()
    if (data) await openDetail(data)
    setSaving(false)
  }

  async function captureFieldSignature() {
    if (!current.sig) return
    if (!current.sigName.trim()) { alert('Enter the printed name of the person signing.'); return }
    await transition(current, {
      status: 'signed', signed_at: new Date().toISOString(),
      signer_name: current.sigName.trim(), signature_data: current.sig,
      client_comments: current.sigComments.trim() || null,
    }, 'signed_in_field', { signer: current.sigName.trim() })
  }

  // ?tab=tm&ticket=<id> (the office email's "Open ticket" link): open that ticket
  // once the list has loaded. A ref keeps openDetail out of the effect deps.
  const openDetailRef = useRef(null)
  openDetailRef.current = openDetail
  const deepLinkDone = useRef(false)
  useEffect(() => {
    if (loading || deepLinkDone.current || !openTicketId) return
    deepLinkDone.current = true
    const hit = tickets.find(x => String(x.id) === String(openTicketId))
    if (hit) openDetailRef.current(hit)
    else setNotice({ kind: 'warn', text: `Ticket ${openTicketId} was not found - it may have been voided or removed.` })
  }, [loading, tickets, openTicketId])

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) return <div style={{ padding: 24, color: 'var(--text3)' }}>Loading T&M tickets…</div>

  const OPEN_STATUSES = ['expected', 'draft', 'submitted', 'out_for_signature', 'signed', 'co_created', 'sent_to_client']
  const shown = tickets.filter(t =>
    (statusFilter === 'all' ? true : statusFilter === 'open' ? OPEN_STATUSES.includes(t.status) : t.status === statusFilter) &&
    (jobFilter ? t.job_id === Number(jobFilter) : true))

  // ── LIST ─────────────────────────────────────────────
  if (view === 'list') return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 17, fontWeight: 700 }}>🎫 T&M Tickets</span>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ New ticket</button>
        {isOffice && (
          <button className="btn btn-sm" title="Create a ticket ahead of time so the foreman knows it must be completed"
            onClick={() => { setForm(blankForm({ status: 'expected' })); setError(''); setView('edit') }}>
            + Expected ticket
          </button>
        )}
        <span style={{ flex: 1 }} />
        <select className="form-input" style={{ width: 170 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="open">Open (not approved)</option>
          <option value="all">All statuses</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <div style={{ width: 260 }}>
          <JobPicker jobs={activeJobs} value={jobFilter} allLabel="All jobs" onPick={setJobFilter} />
        </div>
      </div>
      <NoticeBar notice={notice} onClose={() => setNotice(null)} onRetry={() => notifyOffice(notice.retryId)} />

      {shown.length === 0 && (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
          No tickets{statusFilter !== 'all' ? ' for this filter' : ''} yet. “+ New ticket” starts one; the office can stage
          an “Expected” ticket so the foreman knows ticket work must be written up today.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shown.map(t => {
          const age = daysSince(t.sent_for_signature_at)
          const unsignedFlag = t.status === 'out_for_signature' && age >= 7
          return (
            <div key={t.id} onClick={() => openDetail(t)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fff',
                border: '1px solid #e3e6ea', borderRadius: 8, cursor: 'pointer', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 14, minWidth: 62 }}>#{t.ticket_no}</span>
              {chip(t.status)}
              <span style={{ fontSize: 13, fontWeight: 600 }}>{t.job_num} – {t.job_name}</span>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>{t.ticket_date}</span>
              <span style={{ fontSize: 12, color: '#333', flex: 1, minWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.description || <i style={{ color: '#aaa' }}>no description yet</i>}
              </span>
              {t.pco_number && <span style={{ fontSize: 11, color: '#5B2D9E' }}>PCO {t.pco_number}</span>}
              {t.cor_number && <span style={{ fontSize: 11, color: '#0F6E60' }}>{t.cor_number}</span>}
              {t.status === 'out_for_signature' && t.docusign_status === 'delivered' && <span style={{ fontSize: 11, color: '#5B2D9E' }}>DocuSign: opened by GC</span>}
              {t.status === 'submitted' && t.docusign_status === 'declined' && <span style={{ fontSize: 11, color: '#B42318', fontWeight: 700 }}>GC declined in DocuSign</span>}
              {unsignedFlag && <span style={{ fontSize: 11, color: '#B42318', fontWeight: 700 }}>⚠ unsigned {age}d</span>}
            </div>
          )
        })}
      </div>
    </div>
  )

  // ── EDITOR ───────────────────────────────────────────
  if (view === 'edit') {
    const th = (label, w) => <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--text3)', padding: '4px 6px', width: w }}>{label}</th>
    const td = { padding: '2px 4px', verticalAlign: 'top' }
    const num = { width: 74 }
    const laborTotal = form.labor.reduce((s, r) => s + (Number(r.total_hours) || 0), 0)
    // An office user staging a brand-new Expected ticket only saves it.
    const newExpected = !form.id && form.status === 'expected'
    const footBtn = { flex: '1 1 220px', minHeight: 58, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 2, padding: '8px 12px' }
    const footTitle = { fontSize: 14, fontWeight: 700 }
    const footSub = { fontSize: 11, fontWeight: 400, opacity: 0.85 }
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 14, maxWidth: 980 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>
            {form.id ? `Ticket #${form.ticket_no}` : form.status === 'expected' ? 'New EXPECTED ticket' : 'New T&M ticket'}
          </span>
          {chip(form.status)}
          <span style={{ flex: 1 }} />
          <button className="btn btn-sm" onClick={() => setView('list')}>Cancel</button>
        </div>
        {error && <div style={{ background: '#FEE', color: '#B42318', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <div className="form-group" style={{ minWidth: 260, flex: 2 }}>
            <label className="form-label">Job</label>
            <JobPicker jobs={activeJobs} value={form.job_id}
              onPick={id => setForm(f => applyJobDefaults(f, id, f.ticket_date))} />
          </div>
          <div className="form-group" style={{ width: 160 }}>
            <label className="form-label">Date</label>
            <input className="form-input" type="date" value={form.ticket_date}
              onChange={e => { const v = e.target.value; setForm(f => autoForeman({ ...f, ticket_date: v }, f.job_id, v)) }} />
          </div>
          <div className="form-group" style={{ minWidth: 200, flex: 1 }}>
            <label className="form-label">Foreman{isOffice ? ' *' : ''}</label>
            <select className="form-input" value={form.foreman_name}
              style={form.foreman_err ? { borderColor: '#B42318' } : undefined}
              onChange={e => { const v = e.target.value; setForm(f => ({ ...f, foreman_name: v, foreman_auto: false, foreman_err: '' })) }}>
              {/* Office users start blank (filled from the board); a foreman defaults to himself.
                  Keeps an off-roster saved value selectable. */}
              {form.foreman_name && !(roster || []).some(e => e.name === form.foreman_name) &&
                <option value={form.foreman_name}>{form.foreman_name}</option>}
              <option value="">—</option>
              <optgroup label="Foremen">
                {(roster || []).filter(e => e.active !== false && e.isForeman)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
              </optgroup>
              <optgroup label="All employees">
                {(roster || []).filter(e => e.active !== false && !e.isForeman)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
              </optgroup>
            </select>
            {form.foreman_err && <div style={{ color: '#B42318', fontSize: 12, marginTop: 2 }}>{form.foreman_err}</div>}
            {!form.foreman_err && form.foreman_auto && form.foreman_name && (
              <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2 }}>
                From the schedule for {shortDay(form.ticket_date)} — change it if someone else is doing the work.
              </div>)}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Description &amp; location of extra work</label>
          <textarea className="form-input" rows={3} value={form.description}
            placeholder="What was done and where — column lines, elevations, room numbers. Detail sells the ticket."
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>

        {/* Labor */}
        <div style={{ margin: '14px 0 4px', fontWeight: 700, fontSize: 14 }}>Labor <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text3)' }}>— total {laborTotal} hrs</span></div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{th('Class')}{th('Rate', 90)}{th('Date of work', 130)}{th('# Men', 74)}{th('Hrs/man', 74)}{th('Total', 60)}{th('', 30)}</tr></thead>
          <tbody>
            {form.labor.map((r, i) => (
              <tr key={i}>
                <td style={td}>
                  <CatalogPicker items={catalog.laborBases.map(b => ({ id: b, name: b }))} value={r.labor_class}
                    placeholder="Mason, Carpenter Foreman…"
                    onPick={(v) => setLine('labor', i, { labor_class: v })} />
                </td>
                <td style={td}>
                  <select className="form-input" value={r.rate_variant} onChange={e => setLine('labor', i, { rate_variant: e.target.value })}>
                    {RATE_VARIANTS.map(v => <option key={v}>{v}</option>)}
                  </select>
                </td>
                <td style={td}><input className="form-input" type="date" value={r.date_of_work || form.ticket_date} onChange={e => setLine('labor', i, { date_of_work: e.target.value })} /></td>
                <td style={td}><input className="form-input" style={num} type="number" min="0" step="1" value={r.num_men} onChange={e => setLine('labor', i, { num_men: e.target.value })} /></td>
                <td style={td}><input className="form-input" style={num} type="number" min="0" step="0.5" value={r.hours_per_man} onChange={e => setLine('labor', i, { hours_per_man: e.target.value })} /></td>
                <td style={{ ...td, fontSize: 13, fontWeight: 600, paddingTop: 10 }}>{r.total_hours}</td>
                <td style={td}><button className="btn btn-sm" onClick={() => dropLine('labor', i)}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-sm" style={{ marginTop: 4 }} onClick={() => addLine('labor')}>+ Labor line</button>

        {/* Materials */}
        <div style={{ margin: '16px 0 4px', fontWeight: 700, fontSize: 14 }}>Material</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{th('Item')}{th('UOM', 90)}{th('# of items', 90)}{th('', 30)}</tr></thead>
          <tbody>
            {form.materials.map((r, i) => (
              <tr key={i}>
                <td style={td}>
                  <CatalogPicker items={catalog.materials} value={r.description}
                    placeholder="Start typing — Plexxis material list"
                    onPick={(v) => {
                      const hit = catalog.materials.find(m => m.name === v)
                      setLine('materials', i, { description: v, uom: hit ? (hit.uom || r.uom) : r.uom, catalog_item_id: hit ? hit.id : null })
                    }} />
                </td>
                <td style={td}><input className="form-input" style={{ width: 84 }} value={r.uom || ''} placeholder="EA, YDS…" onChange={e => setLine('materials', i, { uom: e.target.value })} /></td>
                <td style={td}><input className="form-input" style={num} type="number" min="0" step="0.01" value={r.qty} onChange={e => setLine('materials', i, { qty: e.target.value })} /></td>
                <td style={td}><button className="btn btn-sm" onClick={() => dropLine('materials', i)}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-sm" style={{ marginTop: 4 }} onClick={() => addLine('materials')}>+ Material line</button>

        {/* Equipment */}
        <div style={{ margin: '16px 0 4px', fontWeight: 700, fontSize: 14 }}>Equipment</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{th('Equipment')}{th('Hours of use', 100)}{th('# of items', 90)}{th('Total hrs', 70)}{th('', 30)}</tr></thead>
          <tbody>
            {form.equipment.map((r, i) => (
              <tr key={i}>
                <td style={td}>
                  <CatalogPicker items={catalog.equipment} value={r.description}
                    placeholder="Start typing — utility truck, lift, gang box…"
                    onPick={(v) => {
                      const hit = catalog.equipment.find(m => m.name === v)
                      setLine('equipment', i, { description: v, catalog_item_id: hit ? hit.id : null })
                    }} />
                </td>
                <td style={td}><input className="form-input" style={num} type="number" min="0" step="0.5" value={r.hours_of_use} onChange={e => setLine('equipment', i, { hours_of_use: e.target.value })} /></td>
                <td style={td}><input className="form-input" style={num} type="number" min="0" step="1" value={r.qty} onChange={e => setLine('equipment', i, { qty: e.target.value })} /></td>
                <td style={{ ...td, fontSize: 13, fontWeight: 600, paddingTop: 10 }}>{r.total_hours}</td>
                <td style={td}><button className="btn btn-sm" onClick={() => dropLine('equipment', i)}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-sm" style={{ marginTop: 4 }} onClick={() => addLine('equipment')}>+ Equipment line</button>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
          SOP reminder: put EVERYTHING on the ticket — utility truck, gang box, foreman truck, lifts. Never underestimate.
        </div>

        {/* Footer: three ways out of the editor */}
        <div style={{ marginTop: 22, paddingTop: 14, borderTop: '1px solid #e3e6ea' }}>
          {error && <div style={{ background: '#FEE', color: '#B42318', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn" style={footBtn} disabled={saving}
              onClick={() => saveTicket(form.status === 'expected' ? 'expected' : form.status === 'submitted' ? 'submitted' : 'draft')}>
              <span style={footTitle}>💾 {saving ? 'Saving…' : form.status === 'expected' ? 'Save expected ticket' : form.status === 'submitted' ? 'Save changes' : 'Save draft'}</span>
              <span style={footSub}>{form.status === 'submitted' ? 'Stays with the office' : 'Finish it later - nothing is sent'}</span>
            </button>
            {!newExpected && form.status !== 'submitted' && (
              <button className="btn btn-primary" style={footBtn} disabled={saving} onClick={() => saveTicket('submitted')}>
                <span style={footTitle}>📤 Complete &amp; send to office for signature</span>
                <span style={footSub}>Office gets it signed by the GC</span>
              </button>
            )}
            {!newExpected && (
              <button className="btn btn-primary" style={{ ...footBtn, background: '#0F6E60', borderColor: '#0F6E60' }}
                disabled={saving} onClick={startFieldSign}>
                <span style={footTitle}>✍️ Have client sign now (in field)</span>
                <span style={footSub}>Hand the device to the GC super</span>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── DETAIL ───────────────────────────────────────────
  if (view === 'detail' && current) {
    const t = current
    const canEdit = ['expected', 'draft', 'submitted'].includes(t.status)
    const row = { display: 'flex', gap: 8, fontSize: 13, padding: '3px 0' }
    const lbl = { color: 'var(--text3)', minWidth: 120 }
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 14, maxWidth: 980 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>Ticket #{t.ticket_no}</span>
          {chip(t.status)}
          <span style={{ fontSize: 13 }}>{t.job_num} – {t.job_name}</span>
          <span style={{ flex: 1 }} />
          {canEdit && <button className="btn btn-sm" onClick={() => openEdit(t)}>Edit</button>}
          <button className="btn btn-sm" title='Opens a print view — choose "Save as PDF" to attach the signed ticket to the COR'
            onClick={() => printTicket(t)}>🖨 Print / PDF</button>
          <button className="btn btn-sm" onClick={() => setView('list')}>Back</button>
        </div>
        <NoticeBar notice={notice} onClose={() => setNotice(null)} onRetry={() => notifyOffice(notice.retryId)} />

        <div style={row}><span style={lbl}>Date</span><span>{t.ticket_date}</span></div>
        <div style={row}><span style={lbl}>Foreman</span><span>{t.foreman_name || '—'}</span></div>
        <div style={row}><span style={lbl}>Description</span><span style={{ whiteSpace: 'pre-wrap' }}>{t.description || '—'}</span></div>
        {t.pco_number && <div style={row}><span style={lbl}>GC PCO #</span><span>{t.pco_number}</span></div>}
        {t.cor_number && <div style={row}><span style={lbl}>Cornerstone COR</span><span>{t.cor_number}</span></div>}
        {t.signer_name && <div style={row}><span style={lbl}>Signed by</span><span>{t.signer_name}{t.signed_at ? ` on ${new Date(t.signed_at).toLocaleDateString()}` : ''}</span></div>}
        {t.client_comments && <div style={row}><span style={lbl}>Client comments</span><span>{t.client_comments}</span></div>}
        {t.signature_data && <img alt="signature" src={t.signature_data} style={{ maxWidth: 320, border: '1px solid #eee', borderRadius: 6, margin: '6px 0' }} />}

        {/* DocuSign envelope status */}
        {t.docusign_envelope_id && (
          <div style={{ marginTop: 10, padding: '10px 14px', border: '1px solid #d9d2f0', borderRadius: 8, background: '#F6F3FD', fontSize: 13 }}>
            <div><b>DocuSign:</b> {DS_LABEL[t.docusign_status] || t.docusign_status || '—'}
              {t.docusign_note && ['declined', 'voided'].includes(t.docusign_status) && <span> - “{t.docusign_note}”</span>}</div>
            <div style={{ color: 'var(--text3)', marginTop: 2 }}>
              Sent to {t.gc_signer_name} ({t.gc_signer_email}){t.docusign_sent_at ? ` on ${new Date(t.docusign_sent_at).toLocaleString()}` : ''}
              {t.docusign_sent_by ? ` by ${t.docusign_sent_by}` : ''}
            </div>
            {dsActive(t) && t.docusign_webhook === false && (
              <div style={{ color: '#8A4B00', marginTop: 4 }}>Automatic updates are off for this envelope - use Check DocuSign status.</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              {t.signed_pdf_path && <button className="btn btn-sm" onClick={() => openSignedPdf(t)}>⬇ Signed PDF (DocuSign)</button>}
              {isOffice && dsActive(t) && <button className="btn btn-sm" disabled={saving} onClick={() => checkDs(t)}>↻ Check DocuSign status</button>}
              {isOffice && dsActive(t) && <button className="btn btn-sm" style={{ color: '#B42318' }} disabled={saving} onClick={() => voidDs(t)}>Void envelope…</button>}
            </div>
          </div>
        )}

        {/* Lines */}
        <DetailTable title="Labor" headers={['Class', 'Rate', 'Date of work', '# Men', 'Hrs/man', 'Total hrs']}
          rows={t.labor.map(r => [r.labor_class, r.rate_variant, r.date_of_work, r.num_men, r.hours_per_man, <b>{r.total_hours}</b>])} />
        <DetailTable title="Material" headers={['Item', 'Type', 'UOM', 'Qty']}
          rows={t.materials.map(r => {
            const cat = catalog.materials.find(m => m.id === r.catalog_item_id || m.name === r.description)
            return [r.description, cat ? typeLabel(cat) : '', r.uom || '', r.qty]
          })} />
        <DetailTable title="Equipment" headers={['Equipment', 'Hours of use', '# Items', 'Total hrs']}
          rows={t.equipment.map(r => [r.description, r.hours_of_use, r.qty, <b>{r.total_hours}</b>])} />

        {/* Field signature capture */}
        {['draft', 'submitted', 'out_for_signature'].includes(t.status) && !dsActive(t) && (
          <div style={{ marginTop: 18, padding: 14, border: '1px solid #dfe3e8', borderRadius: 8, background: '#fafbfc' }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>✍️ GC signature (right here, on this device)</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
              First choice per the SOP: get it signed on the spot, same day. Hand the device to the super.
            </div>
            <SignaturePad onChange={(sig) => setCurrent(c => ({ ...c, sig }))} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <input className="form-input" style={{ maxWidth: 240 }} placeholder="Print name (signer)"
                value={t.sigName} onChange={e => setCurrent(c => ({ ...c, sigName: e.target.value }))} />
              <input className="form-input" style={{ flex: 1, minWidth: 200 }} placeholder="Client comments (optional — PCO #, notes)"
                value={t.sigComments} onChange={e => setCurrent(c => ({ ...c, sigComments: e.target.value }))} />
              <button className="btn btn-primary" disabled={saving || !t.sig} onClick={captureFieldSignature}>
                {saving ? 'Saving…' : 'Approve & sign'}
              </button>
            </div>
          </div>
        )}

        {/* Office transitions */}
        {/* Send to the GC through DocuSign (office only) */}
        {isOffice && dsCanSend(t) && ds && (
          <div style={{ marginTop: 16, padding: 14, border: '1px solid #d9d2f0', borderRadius: 8, background: '#F6F3FD' }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>✉️ Send to the GC for signature through DocuSign</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
              The GC gets an email from Cornerstone's DocuSign with this ticket to sign. The GC's replies go to the job's PM.
              When the GC signs, the ticket is marked Signed here and the office is emailed the signed copy.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 220, flex: 1 }}>
                <input className="form-input" style={{ width: '100%' }} placeholder="GC signer name (e.g. the super)"
                  value={ds.name} onChange={e => setDs(d => ({ ...d, name: e.target.value }))} />
                {ds.errors.name && <div style={{ color: '#B42318', fontSize: 12 }}>{ds.errors.name}</div>}
              </div>
              <div style={{ minWidth: 240, flex: 1 }}>
                <input className="form-input" style={{ width: '100%' }} type="email" placeholder="GC signer email"
                  value={ds.email} onChange={e => setDs(d => ({ ...d, email: e.target.value }))} />
                {ds.errors.email && <div style={{ color: '#B42318', fontSize: 12 }}>{ds.errors.email}</div>}
              </div>
            </div>
            <textarea className="form-input" rows={2} style={{ width: '100%', marginTop: 8 }} placeholder="Message to the GC (optional)"
              value={ds.message} onChange={e => setDs(d => ({ ...d, message: e.target.value }))} />
            {ds.err && <div style={{ background: '#FEE', color: '#B42318', padding: '6px 10px', borderRadius: 6, marginTop: 8, fontSize: 13 }}>{ds.err}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-primary" disabled={ds.busy} onClick={sendDs}>{ds.busy ? 'Sending…' : 'Send for signature'}</button>
              <button className="btn" disabled={ds.busy} onClick={() => setDs(null)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Office transitions */}
        {isOffice && (
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {t.status === 'expected' && (
              <button className="btn btn-sm" disabled={saving} title="Texts the foreman named on this ticket from the one-way line"
                onClick={async () => { setSaving(true); await textForeman(t.id, true); await refreshCurrent(t.id); setSaving(false) }}>
                📱 Text foreman again
              </button>)}
            {dsCanSend(t) && !ds && (
              <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => openDsForm(t)}>
                ✉️ Send to GC via DocuSign…
              </button>)}
            {t.status === 'submitted' && (
              <button className="btn btn-sm" disabled={saving} onClick={() => transition(t,
                { status: 'out_for_signature', sent_for_signature_at: new Date().toISOString() }, 'sent_for_signature')}>
                📤 Mark sent outside Crew Dispatch (Adobe/paper)
              </button>)}
            {['submitted', 'out_for_signature'].includes(t.status) && (
              <button className="btn btn-sm" disabled={saving} onClick={() => {
                const name = window.prompt('Signed by (print name):', t.signer_name || '')
                if (name === null) return
                transition(t, { status: 'signed', signed_at: new Date().toISOString(), signer_name: name.trim() || null }, 'marked_signed', { signer: name.trim() })
              }}>✅ Mark signed (copy received)</button>)}
            {t.status === 'signed' && (
              <button className="btn btn-sm" disabled={saving} onClick={() => {
                const cor = window.prompt('Plexxis COR # (e.g. COR-00007):', t.cor_number || '')
                if (cor === null || !cor.trim()) return
                transition(t, { status: 'co_created', cor_number: cor.trim(), co_created_at: new Date().toISOString() }, 'co_created', { cor: cor.trim() })
              }}>🧾 COR created in Plexxis…</button>)}
            {t.status === 'co_created' && (
              <button className="btn btn-sm" disabled={saving} onClick={() => transition(t,
                { status: 'sent_to_client', sent_to_client_at: new Date().toISOString() }, 'sent_to_client')}>
                📨 COR sent to client
              </button>)}
            {t.status === 'sent_to_client' && (
              <button className="btn btn-sm" disabled={saving} onClick={() => transition(t,
                { status: 'client_approved', client_approved_at: new Date().toISOString() }, 'client_approved')}>
                🏁 Client approved CO
              </button>)}
            {['signed', 'co_created', 'sent_to_client'].includes(t.status) && !t.pco_number && (
              <button className="btn btn-sm" disabled={saving} onClick={() => {
                const pco = window.prompt('GC PCO # (from client comments / GC log):', '')
                if (pco === null || !pco.trim()) return
                transition(t, { pco_number: pco.trim() }, 'pco_recorded', { pco: pco.trim() })
              }}>PCO #…</button>)}
            {t.status !== 'void' && t.status !== 'client_approved' && (
              <button className="btn btn-sm" style={{ color: '#B42318' }} disabled={saving} onClick={() => {
                if (!window.confirm(`Void ticket #${t.ticket_no}? It stays in the log as Void.`)) return
                transition(t, { status: 'void' }, 'voided')
              }}>Void</button>)}
          </div>
        )}

        {/* History */}
        {t.events.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>History</div>
            {t.events.map(ev => (
              <div key={ev.id} style={{ fontSize: 12, color: 'var(--text3)', padding: '2px 0' }}>
                {new Date(ev.created_at).toLocaleString()} — {ev.event === 'foreman_texted'
                  ? `Foreman texted (${ev.detail?.foreman_name || '?'})` : ev.event.replace(/_/g, ' ')}
                {ev.created_by_name ? ` · ${ev.created_by_name}` : ''}
                {ev.detail && ev.event !== 'foreman_texted' ? ` · ${Object.values(ev.detail).join(', ')}` : ''}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── FIELD SIGN (GC reviews + signs on this device) ───
  if (view === 'sign' && current) {
    const t = current
    const row = { display: 'flex', gap: 8, fontSize: 14, padding: '3px 0' }
    const lbl = { color: 'var(--text3)', minWidth: 110 }
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 14, maxWidth: 980 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 18, fontWeight: 700 }}>Client sign-off - T&amp;M Ticket #{t.ticket_no}</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn-sm" disabled={saving} onClick={() => openEdit(t)}>← Back to edit</button>
        </div>
        <NoticeBar notice={notice} onClose={() => setNotice(null)} onRetry={() => notifyOffice(notice.retryId)} />
        <div style={{ background: '#EEF4FB', border: '1px solid #cfe0f2', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
          Please review the extra work below. Your signature confirms the labor, material and equipment
          listed were used for this work. No pricing is shown on this ticket.
        </div>

        <div style={row}><span style={lbl}>Job</span><span><b>{t.job_num} – {t.job_name}</b></span></div>
        <div style={row}><span style={lbl}>Date</span><span>{t.ticket_date}</span></div>
        <div style={row}><span style={lbl}>Foreman</span><span>{t.foreman_name || '—'}</span></div>
        <div style={row}><span style={lbl}>Description</span><span style={{ whiteSpace: 'pre-wrap' }}>{t.description}</span></div>

        <DetailTable title="Labor" headers={['Class', 'Rate', 'Date of work', '# Men', 'Hrs/man', 'Total hrs']}
          rows={t.labor.map(r => [r.labor_class, r.rate_variant, r.date_of_work, r.num_men, r.hours_per_man, <b>{r.total_hours}</b>])} />
        <DetailTable title="Material" headers={['Item', 'UOM', 'Qty']}
          rows={t.materials.map(r => [r.description, r.uom || '', r.qty])} />
        <DetailTable title="Equipment" headers={['Equipment', 'Hours of use', '# Items', 'Total hrs']}
          rows={t.equipment.map(r => [r.description, r.hours_of_use, r.qty, <b>{r.total_hours}</b>])} />

        <div style={{ marginTop: 18, padding: 14, border: '1px solid #dfe3e8', borderRadius: 8, background: '#fafbfc' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>✍️ Sign here</div>
          <SignaturePad onChange={(sig) => setCurrent(c => ({ ...c, sig }))} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <input className="form-input" style={{ maxWidth: 260 }} placeholder="Print name (required)"
              value={t.sigName} onChange={e => setCurrent(c => ({ ...c, sigName: e.target.value }))} />
            <input className="form-input" style={{ flex: 1, minWidth: 200 }} placeholder="Comments (optional - PCO #, notes)"
              value={t.sigComments} onChange={e => setCurrent(c => ({ ...c, sigComments: e.target.value }))} />
          </div>
          <button className="btn btn-primary" style={{ marginTop: 12, minHeight: 50, width: '100%', maxWidth: 360, fontSize: 15, fontWeight: 700 }}
            disabled={saving || !t.sig || !t.sigName.trim()} onClick={captureFieldSignature}>
            {saving ? 'Saving…' : 'Approve & sign ticket'}
          </button>
        </div>

        {t.status !== 'submitted' && (
          <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            Client can't sign right now?
            <button className="btn btn-sm" disabled={saving} onClick={() => transition(t,
              { status: 'submitted' }, 'submitted', { note: 'client did not sign in field' })}>
              📤 Send to office for signature instead
            </button>
          </div>
        )}
      </div>
    )
  }

  return null
}
